import { Routes, Route, Outlet } from "react-router";
import { Layout } from "@src/presentation/components/Layout/Layout";
import { ProtectedRoute } from "@src/presentation/components/ProtectedRoute/ProtectedRoute";
import { AuthCallback } from "@src/presentation/pages/AuthCallback/AuthCallback";
import { PublicRoute } from "@src/presentation/components/PublicRoute/PublicRoute";
import { AppRoutesGuard } from "./guards/AppRoutes.guard";
import { OrganizationsDataProvider } from "@src/presentation/providers/OrganizationsDataProvider";
import { AppConfig } from "@src/config/AppConfig";
import { Home } from "@src/presentation/pages/Home/Home";
import { Dashboard } from "@src/presentation/pages/Dashboard/Dashboard";
import { Permissions } from "@src/domain/permissions/Permissions";
import { NotFound } from "@src/presentation/pages/NotFound/NotFound";
import { RolesPermissionsProvider } from "@src/presentation/providers/RolesPermissionsProvider";
import { RolePermissions } from "@src/presentation/pages/RolePermissions";
import { UsersAccessDataProvider } from "@src/presentation/providers/UsersAccessDataProvider";
import {
  PAGE_LIMIT,
  UserDetails,
  UsersAccess,
} from "@src/presentation/pages/UsersAccess";
import { Purchasers, PAGE_LIMIT as PURCHASERS_PAGE_LIMIT } from "@src/presentation/pages/Purchasers";
import { PurchasersDataProvider } from "@src/presentation/providers/PurchasersDataProvider";
import {
  OrganizationsPage,
  OrganizationDetailsPage,
  BrandDetails,
} from "@src/presentation/pages/Organizations";
import {
  MasterDataLayout,
  MasterDataLandingPage,
  MasterDataBrandHomePage,
  MasterDataSectionRouter,
} from "@src/presentation/pages/MasterData";
import {
  ConfigLayout,
  ConfigLandingPage,
  ConfigBrandHomePage,
  ConfigSectionRouter,
} from "@src/presentation/pages/Config";

/**
 * App Routes - Presentation layer
 * Single Responsibility: Defines all application routes
 */
export const AppRoutes = () => {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <PublicRoute>
            <Home />
          </PublicRoute>
        }
      />
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <AppRoutesGuard
                requiredPermissions={[Permissions.DASHBOARD_VIEW]}
              >
                <Dashboard />
              </AppRoutesGuard>
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/organizations"
        element={
          <ProtectedRoute>
            <Layout>
              <AppRoutesGuard
                requiredPermissions={[Permissions.ORGANIZATIONS_VIEW]}
              >
                <OrganizationsDataProvider
                  defaultOrganizationsPageSize={AppConfig.getDefaultPaginationLimit()}
                >
                  <Outlet />
                </OrganizationsDataProvider>
              </AppRoutesGuard>
            </Layout>
          </ProtectedRoute>
        }
      >
        <Route index element={<OrganizationsPage />} />
        <Route path=":organizationKey" element={<OrganizationDetailsPage />} />
        <Route
          path=":organizationKey/:brandKey"
          element={
            <AppRoutesGuard requiredPermissions={[Permissions.BRANDS_VIEW]}>
              <BrandDetails />
            </AppRoutesGuard>
          }
        />
      </Route>

      <Route
        path="/users-access"
        element={
          <ProtectedRoute>
            <Layout>
              <AppRoutesGuard
                requiredPermissions={[Permissions.USER_MANAGEMENT_VIEW]}
              >
                <RolesPermissionsProvider>
                  <UsersAccessDataProvider
                    defaultPageNumber={1}
                    defaultUsersPageSize={PAGE_LIMIT}
                  >
                    <Outlet />
                  </UsersAccessDataProvider>
                </RolesPermissionsProvider>
              </AppRoutesGuard>
            </Layout>
          </ProtectedRoute>
        }
      >
        <Route index element={<UsersAccess />} />
        <Route path=":userId" element={<UserDetails />} />
      </Route>

      <Route
        path="/role-permissions"
        element={
          <ProtectedRoute>
            <Layout>
              <AppRoutesGuard
                requiredPermissions={[Permissions.USER_MANAGEMENT_VIEW]}
              >
                <RolesPermissionsProvider>
                  <RolePermissions />
                </RolesPermissionsProvider>
              </AppRoutesGuard>
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/purchasers"
        element={
          <ProtectedRoute>
            <Layout>
              <AppRoutesGuard
                requiredPermissions={[Permissions.PURCHASERS_VIEW]}
              >
                <PurchasersDataProvider
                  defaultPageNumber={1}
                  defaultPageSize={PURCHASERS_PAGE_LIMIT}
                >
                  <Purchasers />
                </PurchasersDataProvider>
              </AppRoutesGuard>
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/master-data"
        element={
          <ProtectedRoute>
            <Layout>
              <AppRoutesGuard
                requiredPermissions={[Permissions.MASTER_DATA_VIEW]}
              >
                <MasterDataLayout />
              </AppRoutesGuard>
            </Layout>
          </ProtectedRoute>
        }
      >
        <Route index element={<MasterDataLandingPage />} />
        <Route
          path=":organizationKey/:brandKey"
          element={<MasterDataBrandHomePage />}
        />
        <Route
          path=":organizationKey/:brandKey/:section"
          element={<MasterDataSectionRouter />}
        />
      </Route>

      <Route
        path="/configuration"
        element={
          <ProtectedRoute>
            <Layout>
              <AppRoutesGuard
                requiredPermissions={[Permissions.CONFIG_VIEW]}
              >
                <ConfigLayout />
              </AppRoutesGuard>
            </Layout>
          </ProtectedRoute>
        }
      >
        <Route index element={<ConfigLandingPage />} />
        <Route
          path=":organizationKey/:brandKey"
          element={<ConfigBrandHomePage />}
        />
        <Route
          path=":organizationKey/:brandKey/:section"
          element={<ConfigSectionRouter />}
        />
      </Route>

      <Route
        path="*"
        element={
          <ProtectedRoute>
            <Layout>
              <NotFound />
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};
